import {
  DataTable,
  FlexBox,
  IconButton,
  MediaPlayer,
  Skeleton,
} from '@dixa/lib-shared-habla';
import { ampli } from 'lib-shared-tracking';
import { ReadWaitMusicApiResponse, WaitMusic } from 'lib-shared-types-api';
import { useMemo } from 'react';

import type { Announcement, ReadAnnouncementsApiResponse } from 'apis/uploads';
import { AppLink } from 'components/appLink';
import {
  translateAnnouncement,
  translateWaitMusic,
} from 'utils/audio/translate';

type AudioType = 'wait-music' | 'announcement';

type Props = {
  retry: VoidFunction;
  type: AudioType;
  isError: boolean;
  isLoading: boolean;
} & (
  | {
      type: 'wait-music';
      data: ReadWaitMusicApiResponse | undefined;
    }
  | {
      type: 'announcement';
      data: ReadAnnouncementsApiResponse | undefined;
    }
);

export const Table = ({
  isLoading,
  isError,
  retry,
  type,
  data,
}: Props): JSX.Element => {
  const items = useMemo(() => {
    const isWaitMusic = type === 'wait-music';
    const items = isWaitMusic
      ? (data as ReadWaitMusicApiResponse | undefined)?.data?.wait_music
      : (data as ReadAnnouncementsApiResponse | undefined)?.data?.uploads;

    return (
      items?.map((item: WaitMusic | Announcement) =>
        isWaitMusic
          ? translateWaitMusic(item as WaitMusic)
          : translateAnnouncement(item as Announcement),
      ) || []
    );
  }, [type, data]);

  return (
    <DataTable
      tableStatusProps={{
        loadingFailed: isError,
        onLoadingRetry: retry,
      }}
      columns={[
        {
          style: { width: '100%', maxWidth: '50%' },
          label: 'Name',
          selector: (item) =>
            isLoading ? <Skeleton height="14px" width="30%" /> : item.name,
        },
        {
          label: 'Preview File',
          selector: (item) =>
            isLoading ? (
              <Skeleton height="56px" width="100%" borderRadius="100" />
            ) : (
              <MediaPlayer
                lazy
                mediaFile={{
                  src: item.url,
                  type: 'audio/mpg',
                }}
              />
            ),
        },
        {
          style: { width: 1 },
          selector: (item) =>
            isLoading ? (
              <FlexBox width="32px" justifyContent="center">
                <Skeleton height="18px" width="18px" />
              </FlexBox>
            ) : (
              <AppLink
                path={`/settings/audio/${type}/:id`}
                params={{ path: { id: item.id } }}
                onClick={() =>
                  ampli.settingsAudioUsageLinkClicked({
                    type,
                    filename: item.name,
                  })
                }
              >
                <IconButton icon="pen" variant="tertiary" />
              </AppLink>
            ),
        },
      ]}
      data={isLoading ? (Array.from({ length: 20 }) as typeof items) : items}
    />
  );
};
